import { CurecallBadge, CurecallModalContainer, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { HRCampaignService } from 'src/services/hr-campaign.service'
import { EHRDelay, THRCampaignDispatch } from 'src/types/hr'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { SmsLogCard } from 'src/components/sms-modal/sms-log-card.component'
import { TSmsLog } from 'src/types/sms-log'
import { TPatient } from 'src/types/patient'
import { capitalizeFirstLetter, getFullName } from 'src/utils/string-formatter.util'
import { twMerge } from 'src/utils/tw-merge.util'
import dayjs from 'dayjs'
import HRSendSmsForm from './hr-send-sms-form.component'

type TCategorizeDelay = Exclude<EHRDelay, EHRDelay.PENDING_REVIEW>

const CATEGORIZE_DELAY_CLASSES: Record<TCategorizeDelay, string> = {
  MIN_30: 'c-bg-green-100 c-text-green-700 hover:c-bg-green-200',
  H1: 'c-bg-yellow-100 c-text-yellow-700 hover:c-bg-yellow-200',
  H2: 'c-bg-orange-200 c-text-orange-700 hover:c-bg-orange-200',
  UNAVAILABLE: 'c-bg-red-100 c-text-red-700 hover:c-bg-red-200',
}

type TDispatchSmsLogsModalProps = Readonly<{
  dispatch: THRCampaignDispatch
  campaignId: number
  onClose: () => void
}>

export const DispatchSmsLogsModal = ({ dispatch, campaignId, onClose }: TDispatchSmsLogsModalProps) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const isPendingReview = dispatch.delay === EHRDelay.PENDING_REVIEW

  const categorizeDelayLabels: Record<TCategorizeDelay, string> = {
    [EHRDelay.MIN_30]: t('pages.hr.campaigns.detail.delay.MIN_30'),
    [EHRDelay.H1]: t('pages.hr.campaigns.detail.delay.H1'),
    [EHRDelay.H2]: t('pages.hr.campaigns.detail.delay.H2'),
    [EHRDelay.UNAVAILABLE]: t('pages.hr.campaigns.detail.delay.UNAVAILABLE'),
  }

  const {
    isLoading,
    error,
    data: smsLogs,
  } = useQuery({
    queryKey: ['hr-sms-logs', project.id, campaignId, dispatch.id],
    queryFn: () => HRCampaignService.getSmsLogs(project.id, campaignId, dispatch.id),
  })

  const { mutate: categorize, isPending: isCategorizing } = useMutation({
    mutationFn: (delay: TCategorizeDelay) =>
      HRCampaignService.updateDispatch(project.id, campaignId, dispatch.id, { delay }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-campaign-dispatches', project.id, campaignId] })
      onClose()
    },
  })

  const contact = dispatch.HRContact
  const contactName =
    getFullName({
      firstname: contact.firstname ?? undefined,
      lastname: contact.lastname ?? undefined,
    }).trim() || contact.phone

  const contactAsPatient: Pick<TPatient, 'id' | 'firstname' | 'lastname'> = {
    id: dispatch.HRContactId,
    firstname: contact.firstname ?? '',
    lastname: contact.lastname ?? '',
  }

  const mappedLogs: TSmsLog[] = (smsLogs ?? []).map((log) => ({
    id: log.id,
    message: log.message,
    inbound: log.inbound,
    sentAt: log.sentAt,
    deliveredAt: log.deliveredAt ?? undefined,
    status: log.status,
    source: null,
    type: 'TEXT',
  }))

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      isOpen
      toggle={onClose}
      dialogClassName="!c-w-[67vw] !c-max-w-[67vw] [&>div]:c-flex [&>div]:c-flex-col"
      bodyClassName="tw-p-0"
    >
      <h1 className="c-pt-6 c-px-8 c-pb-4 c-border-b c-border-b-grey-200">
        {t('pages.hr.campaigns.detail.sms-logs-modal.title')} — {contactName}
      </h1>

      {isLoading && <div className="loading" />}
      {error && <ErrorQueryHandler error={error} />}

      {!isLoading && !error && mappedLogs.length === 0 && (
        <p className="c-bg-grey-50 c-text-base c-w-full c-min-h-[50vh] c-text-center c-pt-36 c-px-8 c-pb-4 c-mb-0">
          {t('pages.hr.campaigns.detail.sms-logs-modal.empty')}
        </p>
      )}

      {!isLoading && !error && mappedLogs.length > 0 && (
        <div className="c-bg-grey-50 c-w-full c-overflow-y-auto c-pt-6 c-px-8 c-pb-4 c-min-h-[50vh] c-max-h-[70vh]">
          <div className="c-flex c-flex-col c-gap-2">
            {mappedLogs.map((smsLog, index) => {
              const prevLog = mappedLogs[index - 1]
              const isNewMonth =
                index === 0 ||
                (prevLog != null &&
                  (!dayjs(smsLog.sentAt).isSame(prevLog.sentAt, 'month') ||
                    !dayjs(smsLog.sentAt).isSame(prevLog.sentAt, 'year')))

              return (
                <div key={smsLog.id} className="c-flex c-flex-col">
                  {isNewMonth && (
                    <CurecallBadge
                      color={ECurecallBadgeColor.SMOOTH_PRIMARY}
                      className="c-my-3 c-self-center"
                    >
                      {capitalizeFirstLetter(dayjs(smsLog.sentAt).format('MMMM YYYY'))}
                    </CurecallBadge>
                  )}
                  <SmsLogCard smsLog={smsLog} patient={contactAsPatient} />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {isPendingReview && (
        <div className="c-px-8 c-py-4 c-border-t c-border-grey-200 c-flex c-flex-col c-gap-3">
          <p className="c-text-sm c-text-grey-600">{t('pages.hr.campaigns.detail.categorize-modal.label')}</p>
          <div className="c-flex c-items-center c-gap-3">
            {(Object.keys(CATEGORIZE_DELAY_CLASSES) as TCategorizeDelay[]).map((delay) => (
              <button
                key={delay}
                disabled={isCategorizing}
                onClick={() => categorize(delay)}
                className={twMerge(
                  'c-px-3 c-py-1.5 c-rounded-full c-text-sm c-font-medium c-transition-colors c-cursor-pointer disabled:c-opacity-50 disabled:c-cursor-not-allowed',
                  CATEGORIZE_DELAY_CLASSES[delay],
                )}
              >
                {categorizeDelayLabels[delay]}
              </button>
            ))}
          </div>
        </div>
      )}

      {(import.meta.env.VITE_ENVIRONMENT === 'dev' || import.meta.env.VITE_ENVIRONMENT === 'staging') && (
        <div className="c-px-8 c-py-4 c-border-t c-border-grey-200">
          <HRSendSmsForm
            hrContactId={dispatch.HRContactId}
            campaignId={campaignId}
            dispatchId={dispatch.id}
            contactName={contactName}
          />
        </div>
      )}
    </CurecallModalContainer>
  )
}
